import { useCallback, useEffect, useState } from "react";

import { useDropzone } from "react-dropzone";

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Download,
  Loader2,
  Upload,
  X,
} from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { downloadBudgetTemplate } from "~/lib/contabilidad/generateBudgetTemplate";
import { parseBudgetFile } from "~/lib/contabilidad/parseBudgetFile";
import { cn } from "~/lib/utils";

import type {
  Account,
  Budget,
  BudgetLineInput,
  BudgetParseResult,
} from "~/types/contabilidad";

interface BudgetImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  budget: Budget;
  companyName: string;
  // Eligible accounts (ingreso/gasto) of the budget's company
  accounts: Account[];
  onImport: (lines: BudgetLineInput[]) => void;
  isImporting: boolean;
}

function formatAmount(value: number): string {
  return new Intl.NumberFormat("es-CL", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function BudgetImportDialog({
  open,
  onOpenChange,
  budget,
  companyName,
  accounts,
  onImport,
  isImporting,
}: BudgetImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<BudgetParseResult | null>(
    null
  );
  const [isParsing, setIsParsing] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setParseResult(null);
    }
  }, [open]);

  const handleParseFile = useCallback(
    async (uploadedFile: File) => {
      setIsParsing(true);
      try {
        const result = await parseBudgetFile(uploadedFile, accounts);
        setParseResult(result);
      } catch (error) {
        console.error("Error parsing budget file:", error);
        setParseResult({
          rows: [],
          validRows: [],
          errorCount: 1,
          warningCount: 0,
        });
      } finally {
        setIsParsing(false);
      }
    },
    [accounts]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const uploadedFile = acceptedFiles[0];
      if (uploadedFile) {
        setFile(uploadedFile);
        handleParseFile(uploadedFile);
      }
    },
    [handleParseFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    maxFiles: 1,
  });

  const handleRemoveFile = () => {
    setFile(null);
    setParseResult(null);
  };

  const handleSubmit = () => {
    if (!parseResult || parseResult.validRows.length === 0) return;

    const lines: BudgetLineInput[] = parseResult.validRows.flatMap((row) =>
      row.amounts.map((amount, monthIndex) => ({
        account_id: row.accountId as string,
        month: monthIndex + 1,
        amount,
      }))
    );

    onImport(lines);
  };

  const canSubmit =
    parseResult && parseResult.errorCount === 0 && parseResult.validRows.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Importar Presupuesto</DialogTitle>
          <DialogDescription>
            Cargue un archivo Excel o CSV con los montos por cuenta y mes. La
            importación reemplaza el presupuesto completo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Archivo de Datos</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  downloadBudgetTemplate(
                    accounts,
                    companyName,
                    budget.fiscal_year
                  )
                }
                className="h-8"
              >
                <Download className="mr-2 h-4 w-4" />
                Descargar Plantilla
              </Button>
            </div>

            {!file ? (
              <div
                {...getRootProps()}
                className={cn(
                  "cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors",
                  isDragActive
                    ? "border-primary bg-primary/5"
                    : "border-muted-foreground/25 hover:border-primary/50"
                )}
              >
                <input {...getInputProps()} />
                <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
                <p className="mt-2 text-sm text-muted-foreground">
                  {isDragActive
                    ? "Suelte el archivo aquí..."
                    : "Arrastre un archivo aquí o haga clic para seleccionar"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Formatos permitidos: .xlsx, .xls, .csv
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-xl border bg-muted/50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-sm font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleRemoveFile}
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {isParsing && (
            <div className="flex items-center justify-center gap-2 py-4">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm text-muted-foreground">
                Procesando archivo...
              </span>
            </div>
          )}

          {parseResult && parseResult.rows.length === 0 && !isParsing && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No se encontraron filas válidas. Verifica que el archivo tenga
                las columnas Codigo, Cuenta y los 12 meses (use la plantilla).
              </AlertDescription>
            </Alert>
          )}

          {parseResult && parseResult.rows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Vista Previa ({parseResult.rows.length} filas)</Label>
                <div className="flex gap-3 text-sm">
                  <span className="text-green-600">
                    {parseResult.validRows.length} a importar
                  </span>
                  {parseResult.warningCount > 0 && (
                    <span className="text-amber-600">
                      {parseResult.warningCount} con advertencias
                    </span>
                  )}
                  {parseResult.errorCount > 0 && (
                    <span className="text-destructive">
                      {parseResult.errorCount} con errores
                    </span>
                  )}
                </div>
              </div>
              <div className="max-h-[300px] overflow-auto rounded-xl border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[60px]">Fila</TableHead>
                      <TableHead className="w-[110px]">Código</TableHead>
                      <TableHead>Cuenta</TableHead>
                      <TableHead className="w-[140px] text-right">
                        Total Anual
                      </TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parseResult.rows.map((row, idx) => (
                      <TableRow
                        key={idx}
                        className={cn(
                          "duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both",
                          row.error && "bg-destructive/10",
                          row.warning && !row.error && "bg-amber-50"
                        )}
                        style={{
                          animationDelay: `${Math.min(idx * 40, 400)}ms`,
                        }}
                      >
                        <TableCell className="text-muted-foreground">
                          {row.rowNumber}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {row.code}
                        </TableCell>
                        <TableCell className="max-w-[240px] truncate">
                          {row.accountName}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {formatAmount(row.total)}
                        </TableCell>
                        <TableCell>
                          {row.error ? (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          ) : row.warning ? (
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {parseResult.errorCount > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-inside list-disc">
                      {parseResult.rows
                        .filter((row) => row.error)
                        .slice(0, 5)
                        .map((row, idx) => (
                          <li key={idx}>
                            Fila {row.rowNumber}: {row.error}
                          </li>
                        ))}
                      {parseResult.errorCount > 5 && (
                        <li>...y {parseResult.errorCount - 5} errores más</li>
                      )}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={isImporting || !canSubmit}
              onClick={handleSubmit}
            >
              {isImporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importando...
                </>
              ) : (
                `Importar ${parseResult?.validRows.length ?? 0} Cuentas`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
